import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Loader2, Plus, Trash2, UserCheck, Users, Zap } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import ConfirmModal from '../../src/components/ConfirmModal';
import { ucCodigoKeys } from '../../src/lib/ucCodigo';
import { derivarStatusBoleto, valorDevido } from '../../src/lib/statusBoleto';
import { buscarTrocasDaUc, type TrocaDonoUc } from '../../src/lib/trocaDonoUc';
import {
  COLUNAS_FATURA_HISTORICO,
  ROTULO_PAPEL,
  atribuirFaturas,
  buscarTitularesDaUc,
  descreverMotivo,
  descreverPeriodo,
  intervaloDasFaturas,
  ordenarTitulares,
  registrarTitularAnterior,
  removerTitularAnterior,
  validarTitularAnterior,
  type FaturaHistorico,
  type GrupoFaturas,
  type PapelTitular,
  type TitularAnteriorForm,
  type TitularUc,
} from '../../src/lib/titularesUc';

/**
 * Titulares da UC (#6238): quem já foi o cliente da Vigor e o titular da conta de
 * luz nesta unidade, e o que cada um deixou em aberto. Fica no topo da aba
 * "Histórico da UC". As regras estão em src/lib/titularesUc.ts.
 */

interface Props {
  ucId: number;
  numeroUc: string;
  outroNumeroUc?: string | null;
  /** Só busca quando a aba aparece: o modal monta todas as abas escondidas. */
  ativo: boolean;
}

const PAPEIS_QUE_REGISTRAM = ['adm', 'gerente', 'analista'];
// Mesma lista da política de uc_titulares: para esses o banco devolve zero linhas.
const PAPEIS_SEM_ACESSO = ['consumidor', 'geradoras_externo', 'empresa_parceira_admin', 'empresa_parceira_operador'];

const reais = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatarDocumento = (doc: string | null | undefined): string => {
  const d = (doc ?? '').replace(/\D/g, '');
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return '';
};

const FORM_VAZIO: TitularAnteriorForm = { nome: '', documento: '', inicio: '', fim: '', justificativa: '' };

const inputCls = 'w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 outline-none focus:border-primary';
const labelCls = 'block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1';

const COR_STATUS: Record<string, string> = {
  Pago: 'text-emerald-600 dark:text-emerald-400',
  Quitado: 'text-emerald-600 dark:text-emerald-400',
  Vencido: 'text-red-600 dark:text-red-400',
  'Em aberto': 'text-amber-600 dark:text-amber-400',
  Parcelado: 'text-indigo-600 dark:text-indigo-400',
};

const ResumoDebito: React.FC<{ grupo: GrupoFaturas }> = ({ grupo }) => {
  const r = grupo.resumo;
  if (grupo.faturas.length === 0) return <p className="text-xs text-gray-400 dark:text-gray-500">Nenhuma fatura neste período.</p>;
  if (r.cobrancas === 0) {
    return <p className="text-xs text-gray-500 dark:text-gray-400">{grupo.faturas.length} {grupo.faturas.length === 1 ? 'fatura' : 'faturas'}, nenhuma virou cobrança.</p>;
  }
  return (
    <p className="text-xs text-gray-600 dark:text-gray-300">
      {r.cobrancas} {r.cobrancas === 1 ? 'cobrança' : 'cobranças'} · {r.pagas} {r.pagas === 1 ? 'paga' : 'pagas'}
      {r.parceladas > 0 ? ` · ${r.parceladas} em acordo` : ''}
      {' · '}
      {r.emAberto === 0 ? (
        <span className="font-semibold text-emerald-600 dark:text-emerald-400">nada em aberto</span>
      ) : (
        <span className={`font-bold ${r.vencidas > 0 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
          {reais(r.valorEmAberto)} em aberto
          {r.vencidas > 0 ? ` (${r.vencidas} ${r.vencidas === 1 ? 'vencida' : 'vencidas'}: ${reais(r.valorVencido)})` : ''}
        </span>
      )}
    </p>
  );
};

const TabelaFaturas: React.FC<{ faturas: FaturaHistorico[] }> = ({ faturas }) => {
  const agora = new Date();
  const ordenadas = [...faturas].sort((a, b) => String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')));
  return (
    <div className="mt-3 overflow-x-auto rounded-lg border border-gray-100 dark:border-gray-700/60">
      <table className="w-full text-xs">
        <thead className="bg-gray-50 dark:bg-gray-800/60 text-gray-500 dark:text-gray-400">
          <tr>
            <th className="px-3 py-2 text-left font-semibold">Mês</th>
            <th className="px-3 py-2 text-left font-semibold">Situação</th>
            <th className="px-3 py-2 text-right font-semibold">Valor</th>
            <th className="px-3 py-2 text-left font-semibold">Vencimento</th>
            <th className="px-3 py-2 text-left font-semibold">Nome no boleto</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {ordenadas.map(f => {
            const status = derivarStatusBoleto(f, agora);
            const venc = (f.vencimento_boleto || f.data_vencimento || '').split('T')[0].split('-').reverse().join('/');
            return (
              <tr key={f.id}>
                <td className="px-3 py-1.5 font-semibold text-gray-800 dark:text-gray-200">{f.mes_referencia || '—'}</td>
                <td className={`px-3 py-1.5 font-semibold ${COR_STATUS[status] || 'text-gray-400 dark:text-gray-500'}`}>{status}</td>
                <td className="px-3 py-1.5 text-right text-gray-700 dark:text-gray-300">{valorDevido(f) > 0 ? reais(valorDevido(f)) : '—'}</td>
                <td className="px-3 py-1.5 text-gray-600 dark:text-gray-400">{venc || '—'}</td>
                <td className="px-3 py-1.5 text-gray-600 dark:text-gray-400">{f.nome_cliente_boleto || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const TitularesUcSecao: React.FC<Props> = ({ ucId, numeroUc, outroNumeroUc, ativo }) => {
  const { profile } = useAuth();
  const { addToast } = useToast();
  const podeRegistrar = PAPEIS_QUE_REGISTRAM.includes(profile?.role ?? '');
  const semAcesso = !profile?.role || PAPEIS_SEM_ACESSO.includes(profile.role);

  const [titulares, setTitulares] = useState<TitularUc[]>([]);
  const [faturas, setFaturas] = useState<FaturaHistorico[]>([]);
  const [trocas, setTrocas] = useState<TrocaDonoUc[]>([]);
  const [estado, setEstado] = useState<'ocioso' | 'carregando' | 'pronto' | 'erro'>('ocioso');
  const [versao, setVersao] = useState(0);
  const [abertos, setAbertos] = useState<Record<string, boolean>>({});

  const [form, setForm] = useState<TitularAnteriorForm | null>(null);
  const [papelForm, setPapelForm] = useState<PapelTitular>('cliente');
  const [salvando, setSalvando] = useState(false);
  const [remover, setRemover] = useState<TitularUc | null>(null);
  const [removendo, setRemovendo] = useState(false);

  // UC nova no modal: volta ao começo.
  useEffect(() => {
    setEstado('ocioso');
    setTitulares([]); setFaturas([]); setTrocas([]);
    setAbertos({}); setForm(null);
  }, [ucId]);

  useEffect(() => {
    if (!ativo || !ucId || semAcesso) return;
    let vivo = true;
    setEstado(e => (e === 'pronto' ? e : 'carregando'));
    const chaves = ucCodigoKeys(numeroUc, outroNumeroUc);
    Promise.all([
      buscarTitularesDaUc(supabase, ucId),
      buscarTrocasDaUc(supabase, ucId).catch(() => [] as TrocaDonoUc[]),
      chaves.length === 0
        ? Promise.resolve([] as FaturaHistorico[])
        : supabase
            .from('faturas')
            .select(COLUNAS_FATURA_HISTORICO)
            .in('uc_codigo_norm', chaves)
            .eq('ambiente', 'producao')
            .is('excluido_em', null)
            .then(({ data, error }) => { if (error) throw error; return (data ?? []) as unknown as FaturaHistorico[]; }),
    ])
      .then(([t, tr, f]) => {
        if (!vivo) return;
        setTitulares(t); setTrocas(tr); setFaturas(f); setEstado('pronto');
      })
      .catch(err => {
        console.error('Titulares da UC: não foi possível carregar', err);
        if (vivo) setEstado('erro');
      });
    return () => { vivo = false; };
  }, [ativo, ucId, numeroUc, outroNumeroUc, versao, semAcesso]);

  const grupos = useMemo(() => atribuirFaturas(faturas, titulares, trocas), [faturas, titulares, trocas]);
  const titularesConta = useMemo(() => ordenarTitulares(titulares.filter(t => t.papel === 'titular_conta')), [titulares]);
  const semTitular = grupos.filter(g => !g.titular);

  const abrirForm = (pre?: Partial<TitularAnteriorForm>, papel: PapelTitular = 'cliente') => {
    setPapelForm(papel);
    setForm({ ...FORM_VAZIO, ...pre });
  };

  const salvar = async () => {
    if (!form) return;
    const erro = validarTitularAnterior(form);
    if (erro) { addToast(erro, 'warning'); return; }
    setSalvando(true);
    try {
      await registrarTitularAnterior(supabase, ucId, form, papelForm);
      addToast('Titular anterior registrado no histórico da UC.', 'success');
      setForm(null);
      setVersao(v => v + 1);
    } catch (err: any) {
      addToast(err?.message || 'Não foi possível registrar o titular anterior.', 'error');
    } finally {
      setSalvando(false);
    }
  };

  const confirmarRemocao = async () => {
    if (!remover) return;
    setRemovendo(true);
    try {
      await removerTitularAnterior(supabase, remover.id);
      addToast('Registro removido do histórico.', 'success');
      setRemover(null);
      setVersao(v => v + 1);
    } catch (err: any) {
      addToast(err?.message || 'Não foi possível remover o registro.', 'error');
    } finally {
      setRemovendo(false);
    }
  };

  if (semAcesso || estado === 'ocioso') return null;

  const cabecalhoPessoa = (t: TitularUc) => (
    <>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${t.fim
          ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
          : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'}`}>
          {t.fim ? 'Anterior' : 'Atual'}
        </span>
        <span className="text-sm font-bold text-gray-900 dark:text-white">{t.nome || 'Sem nome no cadastro'}</span>
        {t.cpf_cnpj && <span className="text-xs text-gray-500 dark:text-gray-400">{formatarDocumento(t.cpf_cnpj)}</span>}
        <span className="text-xs text-gray-500 dark:text-gray-400">· {descreverPeriodo(t)}</span>
        {t.inicio_estimado && (
          <span
            className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
            title="A data de início não foi registrada na época. Esta é a data do cadastro da UC (ou a data em que o titular anterior saiu); o início real pode ser outro."
          >
            data aproximada
          </span>
        )}
      </div>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {descreverMotivo(t)}
        {t.motivo !== 'registro_manual' && t.motivo !== 'carga_inicial' && t.registrado_por_nome ? ` · por ${t.registrado_por_nome}` : ''}
        {t.negociacao_id ? ` · negociação #${t.negociacao_id}` : ''}
        {t.fim && (t.contatos?.whatsapp || t.contatos?.email)
          ? ` · contato na época: ${[t.contatos.whatsapp, t.contatos.email].filter(Boolean).join(' / ')}`
          : ''}
      </p>
      {t.motivo === 'registro_manual' && t.observacao && (
        <p className="mt-1 text-xs italic text-gray-500 dark:text-gray-400">"{t.observacao}"</p>
      )}
    </>
  );

  const botaoRemover = (t: TitularUc) =>
    podeRegistrar && t.motivo === 'registro_manual' && t.fim ? (
      <button
        type="button"
        onClick={() => setRemover(t)}
        title="Remover este registro feito à mão"
        className="p-1.5 text-gray-400 hover:text-red-500 rounded transition-colors"
      >
        <Trash2 size={14} />
      </button>
    ) : null;

  return (
    <div className="px-6 md:px-8 pt-6 bg-gray-50 dark:bg-gray-900/20">
      <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/60 shadow-sm p-4 md:p-5">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h3 className="flex items-center gap-2 text-[15px] font-bold text-gray-900 dark:text-white">
            <Users size={16} className="text-primary" /> Titulares desta UC
          </h3>
          {podeRegistrar && estado === 'pronto' && !form && (
            <button
              type="button"
              onClick={() => abrirForm()}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-primary border border-primary/30 hover:bg-primary/10 rounded-lg transition-colors shrink-0"
            >
              <Plus size={13} /> Registrar titular anterior
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          A UC é sempre a mesma; quem responde por ela pode mudar. Cada cobrança fica com quem está no boleto.
        </p>

        {estado === 'carregando' && (
          <div className="flex items-center gap-2 py-6 text-sm text-gray-500"><Loader2 size={16} className="animate-spin text-primary" /> Carregando os titulares...</div>
        )}
        {estado === 'erro' && (
          <p className="py-4 text-sm text-red-500">Não foi possível carregar os titulares desta UC.</p>
        )}

        {estado === 'pronto' && form && (
          <div className="mb-4 p-3 rounded-xl border border-primary/30 bg-primary/5">
            <p className="text-xs font-bold text-gray-800 dark:text-gray-100 mb-1">Registrar quem já foi titular desta UC</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Para o que aconteceu antes de o sistema guardar este histórico. Só anota o período: não muda fatura, boleto nem o cadastro atual.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className={labelCls}>Era o quê</label>
                <select value={papelForm} onChange={e => setPapelForm(e.target.value as PapelTitular)} className={inputCls}>
                  <option value="cliente">{ROTULO_PAPEL.cliente} (assinava e pagava)</option>
                  <option value="titular_conta">{ROTULO_PAPEL.titular_conta} (nome na Equatorial)</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Nome *</label>
                <input value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>CPF / CNPJ</label>
                <input value={form.documento} onChange={e => setForm({ ...form, documento: e.target.value })} placeholder="Se souber" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>De *</label>
                <input type="date" value={form.inicio} onChange={e => setForm({ ...form, inicio: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Até *</label>
                <input type="date" value={form.fim} onChange={e => setForm({ ...form, fim: e.target.value })} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Como você sabe disso *</label>
                <textarea
                  value={form.justificativa}
                  onChange={e => setForm({ ...form, justificativa: e.target.value })}
                  rows={2}
                  placeholder="Ex.: contrato de locação de 2024 no nome dele; a UC passou para o filho em março."
                  className={`${inputCls} resize-y`}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button type="button" onClick={() => setForm(null)} disabled={salvando} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
                Cancelar
              </button>
              <button type="button" onClick={salvar} disabled={salvando} className="px-3 py-1.5 text-xs font-bold rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-60 flex items-center gap-1.5">
                {salvando && <Loader2 size={12} className="animate-spin" />} Registrar
              </button>
            </div>
          </div>
        )}

        {estado === 'pronto' && (
          <>
            <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
              <UserCheck size={13} /> {ROTULO_PAPEL.cliente} <span className="font-normal normal-case tracking-normal">— quem assina e paga</span>
            </p>
            {grupos.filter(g => g.titular).length === 0 && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">O cadastro desta UC não tem cliente informado.</p>
            )}
            <div className="space-y-2">
              {grupos.map(g => {
                const aberto = !!abertos[g.chave];
                return (
                  <div
                    key={g.chave}
                    className={`rounded-lg border p-3 ${g.titular
                      ? 'border-gray-100 dark:border-gray-700/60'
                      : 'border-amber-200 dark:border-amber-800/40 bg-amber-50/60 dark:bg-amber-900/10'}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        {g.titular ? cabecalhoPessoa(g.titular) : (
                          <>
                            <p className="flex items-start gap-1.5 text-sm font-bold text-amber-900 dark:text-amber-200">
                              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                              <span>
                                Boletos no nome de {g.pagador?.nome}
                                {g.pagador?.documento ? <span className="font-normal text-xs"> · {formatarDocumento(g.pagador.documento)}</span> : null}
                              </span>
                            </p>
                            <p className="mt-1 text-xs text-amber-800/90 dark:text-amber-300/90">
                              Não consta como titular desta UC. Pode ser um titular antigo que o sistema não registrou, ou alguém que paga por ela (o assinante da negociação, por exemplo).
                            </p>
                          </>
                        )}
                        <div className="mt-2"><ResumoDebito grupo={g} /></div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {g.titular && botaoRemover(g.titular)}
                        {g.faturas.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setAbertos(a => ({ ...a, [g.chave]: !aberto }))}
                            className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                          >
                            {aberto ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Faturas
                          </button>
                        )}
                      </div>
                    </div>
                    {!g.titular && podeRegistrar && !form && (
                      <button
                        type="button"
                        onClick={() => abrirForm({
                          nome: g.pagador?.nome ?? '',
                          documento: formatarDocumento(g.pagador?.documento),
                          ...(intervaloDasFaturas(g.faturas) ?? {}),
                        })}
                        className="mt-2 text-[11px] font-semibold text-amber-800 dark:text-amber-300 underline underline-offset-2 hover:text-amber-900"
                      >
                        Era titular antes? Registrar como titular anterior
                      </button>
                    )}
                    {aberto && <TabelaFaturas faturas={g.faturas} />}
                  </div>
                );
              })}
            </div>

            <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mt-5 mb-2">
              <Zap size={13} /> {ROTULO_PAPEL.titular_conta} <span className="font-normal normal-case tracking-normal">— o nome na Equatorial</span>
            </p>
            {titularesConta.length === 0 ? (
              <p className="text-xs text-gray-400 dark:text-gray-500">O cadastro desta UC não tem titular da conta de luz informado.</p>
            ) : (
              <div className="space-y-2">
                {titularesConta.map(t => (
                  <div key={t.id} className="rounded-lg border border-gray-100 dark:border-gray-700/60 p-3 flex items-start justify-between gap-2">
                    <div className="min-w-0">{cabecalhoPessoa(t)}</div>
                    {botaoRemover(t)}
                  </div>
                ))}
              </div>
            )}

            {semTitular.length === 0 && grupos.filter(g => g.titular).length <= 1 && titularesConta.length <= 1 && (
              <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
                Nenhuma troca de titular registrada nesta UC.
              </p>
            )}
          </>
        )}
      </div>

      <ConfirmModal
        isOpen={!!remover}
        onClose={() => setRemover(null)}
        onConfirm={confirmarRemocao}
        loading={removendo}
        title="Remover este registro?"
        message={`${remover?.nome ?? 'O titular'} sai do histórico desta UC. Só dá para remover o que foi registrado à mão; faturas e boletos não mudam.`}
        confirmLabel="Remover"
        variant="warning"
      />
    </div>
  );
};

export default TitularesUcSecao;
